import { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import CustomButton from '../customButton';
import AlertDialog from '../alertDialog';
import DroppableAttributeQuestionsList from '../droppableAttributeQuestionsList';
import { ReactComponent as QuestionsIcon } from '../../../assets/icons/live_help.svg';
import { ReactComponent as ArchiveIcon } from '../../../assets/icons/archive.svg';
import {
  createSingleQuestion,
  duplicateQuestion,
  reorder,
  isArrayEmpty,
  asyncDebounce,
  getItemById,
  replaceObjectInList,
  removeObjectFromArray,
} from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: { width: '100%' },
  labelWrapper: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'none',
    cursor: 'pointer',
  },
  selectedLabelWrapper: {
    borderBottom: `4px solid ${primary.bluish1}`,
    paddingBottom: spacing(3.75),
  },
  buttonWrapper: {
    height: spacing(6),
  },
  labelWrapperBlock: {
    justifyContent: 'space-between',
    display: 'flex',
    marginBottom: spacing(4.5),
    paddingBottom: spacing(3),
    height: 30,
    borderBottom: `1px solid ${primary.bluish5}`,
  },
  questionsIcon: {
    width: 24,
    height: 24,
    paddingRight: spacing(2),
  },
  disabledArchivedButton: {
    pointerEvents: 'none',
    opacity: '30%',
  },
}));

const ADD_QUESTION_DELAY = 250;
const QUESTIONS_DEFAULT_NAME = 'questions';
const QUESTION_NUMBER_DEFAULT_NAME = 'question_count';

const AddAttributeQuestions = ({
  translations,
  attribute,
  initialAttribute,
  dialogs,
  switchHorizontalWarning,
  setDialogVisibility,
  updateAttributeQuestion,
  hasError = {},
  isQuestionView,
  onChangeQuestionView,
  isEditDisabled,
}) => {
  const classes = useStyles();
  const {
    confirmQuestionDeletionOpened,
    confirmQuestionDuplicateOpened,
    confirmQuestionArchiveOpened,
    confirmQuestionUnarchiveOpened,
  } = dialogs;

  const [questionId, setQuestionId] = useState(-1);
  const [question, setQuestion] = useState(null);

  const { questions, answer_count } = attribute;
  const activeQuestions = questions?.filter(q => !q.is_archived);
  const hasQuestions = !isArrayEmpty(activeQuestions);
  const hasArchivedQuestions = Boolean(
    attribute.questions.find(q => q.is_archived)
  );

  const onUpdateQuestion = updatedQuestion => {
    const updatedData = {
      ...attribute,
      questions: replaceObjectInList(
        attribute.questions,
        attribute.questions.findIndex(
          q => q.unique_id === updatedQuestion.unique_id
        ),
        updatedQuestion
      ),
    };
    if (!isQuestionView) {
      const archivedQuestions = updatedData.questions?.filter(
        q => q.is_archived
      );
      if (!archivedQuestions.length) {
        onChangeQuestionView(true);
      }
    }
    updateAttributeQuestion([QUESTIONS_DEFAULT_NAME], updatedData);
  };

  const onDuplicateQuestion = currentQuestion => {
    setQuestion(currentQuestion);

    setDialogVisibility({
      dialogName: 'confirmQuestionDuplicate',
      opened: true,
    });
  };

  const onArchiveQuestion = currentQuestion => {
    setQuestion(currentQuestion);
    setDialogVisibility({
      dialogName: 'confirmQuestionArchive',
      opened: true,
    });
  };

  const createQuestion = newQuestion => {
    const modifiedQuestionList = [...attribute.questions, newQuestion];

    return updateAttributeQuestion(
      [QUESTION_NUMBER_DEFAULT_NAME, QUESTIONS_DEFAULT_NAME],
      {
        ...attribute,
        question_count: modifiedQuestionList.length,
        questions: modifiedQuestionList,
      }
    );
  };

  const handleSaveDuplicate = () => {
    const data = getItemById(
      attribute.questions,
      question.unique_id,
      'unique_id'
    );

    createQuestion(
      duplicateQuestion(data, attribute.questions.length + 1, attribute)
    );
    setDialogVisibility({
      dialogName: 'confirmQuestionDuplicate',
      opened: false,
    });
  };

  const onDeleteQuestion = currentQuestion => {
    setQuestionId(currentQuestion.unique_id);
    setDialogVisibility({
      dialogName: 'confirmQuestionDeletion',
      opened: true,
    });
  };

  const onDragEnd = result => {
    if (
      !result.destination ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const newOrder = reorder(
      attribute.questions,
      result.source.index,
      result.destination.index
    );

    updateAttributeQuestion([QUESTIONS_DEFAULT_NAME], {
      questions: newOrder.map((q, index) => ({
        ...q,
        order_id: index + 1,
      })),
    });
  };

  const onChangeView = value => {
    onChangeQuestionView(value);
  };

  const onAddNewQuestion = () => {
    const answerCount = attribute.text_reply_questions ? 0 : answer_count;
    const emptyQuestion = createSingleQuestion(
      translations.placeholders.questionTitle,
      questions.length + 1,
      answerCount,
      attribute
    );

    createQuestion(emptyQuestion);
  };

  const deleteAttributeQuestion = qUniqueId => {
    const questionTemp = attribute.questions.find(
      q => q.unique_id === qUniqueId
    );
    const modifiedQuestionList = removeObjectFromArray(
      attribute.questions,
      questionTemp,
      'unique_id'
    );

    return updateAttributeQuestion(
      [QUESTION_NUMBER_DEFAULT_NAME, QUESTIONS_DEFAULT_NAME],
      {
        ...attribute,
        question_count: modifiedQuestionList.length,
        questions: modifiedQuestionList.map((q, index) => ({
          ...q,
          order_id: index + 1,
        })),
      }
    );
  };

  const onQuestionUnarchive = currentQuestion => {
    setQuestion(currentQuestion);
    setDialogVisibility({
      dialogName: 'confirmQuestionUnarchive',
      opened: true,
    });
  };

  const archiveAttributeQuestion = value => {
    onUpdateQuestion({ ...question, is_archived: value });
  };

  return (
    <div className={classes.main}>
      <div className={classes.labelWrapperBlock}>
        <div
          className={classNames(classes.labelWrapper, classes.buttonWrapper, {
            [classes.selectedLabelWrapper]: isQuestionView,
          })}
        >
          <div
            onClick={() => onChangeView(true)}
            className={classNames(classes.labelWrapper)}
          >
            <QuestionsIcon className={classes.questionsIcon} />
            <Typography variant="h4">{translations.questions}</Typography>
          </div>
        </div>
        <div
          className={classNames(classes.labelWrapper, classes.buttonWrapper, {
            [classes.selectedLabelWrapper]: !isQuestionView,
            [classes.disabledArchivedButton]: !hasArchivedQuestions,
          })}
          onClick={() => onChangeView(false)}
        >
          <ArchiveIcon className={classes.questionsIcon} />
          <Typography variant="h4">{translations.archived}</Typography>
        </div>
      </div>
      <DroppableAttributeQuestionsList
        onDragEnd={onDragEnd}
        onDeleteQuestion={onDeleteQuestion}
        onUpdateQuestion={onUpdateQuestion}
        switchHorizontalWarning={switchHorizontalWarning}
        onDuplicateQuestion={onDuplicateQuestion}
        attribute={attribute}
        questions={questions}
        initialAttribute={initialAttribute}
        hasError={hasError}
        isQuestionView={isQuestionView}
        isEditDisabled={isEditDisabled}
        onArchiveQuestion={onArchiveQuestion}
        onQuestionUnarchive={onQuestionUnarchive}
        translations={translations}
        hasQuestions={hasQuestions}
      />
      <CustomButton
        type="addWithTextDarkSlimNew"
        onClick={asyncDebounce(onAddNewQuestion, ADD_QUESTION_DELAY)}
        fullWidth
      >
        {translations.newQuestion}
      </CustomButton>
      <AlertDialog
        translations={translations.archiveQuestionDialog}
        isOpened={confirmQuestionArchiveOpened}
        onClose={() => {
          setDialogVisibility({
            dialogName: 'confirmQuestionArchive',
            opened: false,
          });
        }}
        onConfirm={() => {
          archiveAttributeQuestion(true);
          setDialogVisibility({
            dialogName: 'confirmQuestionArchive',
            opened: false,
          });
        }}
        isWarning
      />
      <AlertDialog
        translations={translations.deleteQuestionDialog}
        isOpened={confirmQuestionDeletionOpened}
        onClose={() => {
          setDialogVisibility({
            dialogName: 'confirmQuestionDeletion',
            opened: false,
          });
        }}
        onConfirm={() => {
          deleteAttributeQuestion(questionId);
          setDialogVisibility({
            dialogName: 'confirmQuestionDeletion',
            opened: false,
          });
        }}
        isWarning
      />
      <AlertDialog
        translations={translations.duplicateQuestionDialog}
        isOpened={confirmQuestionDuplicateOpened}
        onClose={() => {
          setDialogVisibility({
            dialogName: 'confirmQuestionDuplicate',
            opened: false,
          });
        }}
        onConfirm={handleSaveDuplicate}
      />
      <AlertDialog
        translations={translations.unarchiveQuestionDialog}
        isOpened={confirmQuestionUnarchiveOpened}
        onClose={() => {
          setDialogVisibility({
            dialogName: 'confirmQuestionUnarchive',
            opened: false,
          });
        }}
        onConfirm={() => {
          archiveAttributeQuestion(false);
          setDialogVisibility({
            dialogName: 'confirmQuestionUnarchive',
            opened: false,
          });
        }}
      />
    </div>
  );
};

AddAttributeQuestions.propTypes = {
  translations: PropTypes.object.isRequired,
  attribute: PropTypes.object.isRequired,
  initialAttribute: PropTypes.object.isRequired,
  dialogs: PropTypes.object.isRequired,
  updateAttributeQuestion: PropTypes.func.isRequired,
  setDialogVisibility: PropTypes.func.isRequired,
  switchHorizontalWarning: PropTypes.func.isRequired,
  hasError: PropTypes.object,
  isQuestionView: PropTypes.bool.isRequired,
  onChangeQuestionView: PropTypes.func.isRequired,
  isEditDisabled: PropTypes.bool.isRequired,
};

export default AddAttributeQuestions;
